/**
 * The typed API client.
 *
 * A factory rather than a module singleton, because the three platform seams —
 * base URL, auth headers, device identity — differ per client and are injected
 * as an `ApiHost`. See host.ts for why that is not over-abstraction.
 *
 * Every request goes through `host.url()`, so the same code works same-origin
 * in a browser, against an absolute origin in the desktop shell, and against a
 * configured host in React Native.
 */
import { ApiError } from './error.js';
import type { ApiHost } from './host.js';
import type {
  AiAuditRecord, AiStatusResponse, EntitlementReport, ErrorGroup, ErrorOccurrence,
  NewsStatusResponse,
  Account,
  AlertTrigger,
  AlertsResponse,
  Analysis,
  Analytics,
  AuthSuccess,
  BarsResponse,
  BrokerChallenge,
  BrokerConnectionRow,
  DevicesResponse,
  DrawingDto,
  DrawingsResponse,
  Health,
  Instrument,
  JournalEntry,
  Me,
  Preferences,
  PreferencesResponse,
  PlansResponse,
  RuleState,
  SessionRow,
  SubscriptionResponse,
  Trade,
  ScenariosResponse, ScoreResponse, StrategiesResponse, StrategyDto, StrategyInput,

  ChecklistResponse, ConsistencyResponse, NotebookEntryDto, NotebookEntryInput, TemplateDto,

  BreachDto, Dimension, DimensionsResponse, ReportResponse, ReportSpec, SavedReportDto,
  TradeFilterDto, WinLossResponse,

  AttachmentDto, ShareDto,

  AgentId, AgentRunDto, AgentsResponse,

  BacktestCapabilities, BacktestComparison, BacktestDto, BacktestInput,

  DepthResponse, FineReplayResponse, FootprintResponse, ProfileResponse, TapeResponse,
  WorkspaceDto, WorkspaceInput, WorkspacesResponse,

  StatisticsResponse, TagDto, TagKind, TagsResponse, TagSuggestionsResponse,
  CreditsResponse, PersonalAnalysisResponse,
  NotificationPrefs,
  NewsCalendarResponse,
  NewsFeedResponse,
  ReleaseCard,
  ReactionStudy,
  ReleaseVerdict,
  SentimentReading,
  WatchlistResponse,
  AiAuditStats, ResolvedRange,
  SnapshotDay,
  ImportPreview, ImportRequest, ImportResultDto, TradeContextDto,
  OnboardingState, OnboardingFirm, OnboardingAnswersDto, OnboardingSaveResponse,
  PortfolioRiskResponse,
  PrecedentResponse,
  ScriptsResponse, ScriptSaveResponse, SharedScriptDto,
  MyScriptDto, InstalledScriptDto,} from './types.js';
import type { WidgetSnapshot } from '@aegis/core';

export function createApi(host: ApiHost) {
  let onUnauthenticated: (() => void) | null = null;

  function setUnauthenticatedHandler(fn: (() => void) | null): void {
    onUnauthenticated = fn;
  }

  async function toError(res: Response): Promise<ApiError> {
    const body = (await res.json().catch(() => ({}))) as {
      error?: string; message?: string; retryAfterSec?: number;
    };
    const err = new ApiError(
      res.status,
      body.error ?? 'unknown',
      body.message ?? res.statusText,
      body.retryAfterSec,
    );
    if (err.unauthenticated) onUnauthenticated?.();
    return err;
  }

  async function get<T>(path: string): Promise<T> {
    const res = await fetch(host.url(path), {
      headers: { accept: 'application/json', ...host.authHeaders() },
    });
    if (!res.ok) throw await toError(res);
    return (await res.json()) as T;
  }

  async function send<T>(
    path: string, method: 'POST' | 'PUT' | 'PATCH' | 'DELETE', body?: unknown,
  ): Promise<T> {
    // `content-type: application/json` with no body is a 400 from Fastify —
    // "Body cannot be empty when content-type is set to 'application/json'". Half
    // the mutating endpoints here take no body (logout, portal, sync, device
    // reactivate), and the failure is quiet: `logout()` still clears local state
    // in its `finally`, so the user appears signed out while the session is very
    // much alive on the server.
    const headers: Record<string, string> = { accept: 'application/json', ...host.authHeaders() };
    if (body !== undefined) headers['content-type'] = 'application/json';

    const res = await fetch(host.url(path), {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    if (!res.ok) throw await toError(res);
    // 204 and 205 carry no body, and `res.json()` on an empty one throws a
    // parse error that looks like a server fault. Several endpoints here answer
    // 204 by design — an assignment that succeeded has nothing to say.
    if (res.status === 204 || res.status === 205) return undefined as T;
    return (await res.json()) as T;
  }

  const api = {
    health: () => get<Health>('/health'),
    instruments: () => get<{ instruments: Instrument[] }>('/v1/instruments'),
    /**
     * @param before the time in seconds of the OLDEST bar you already hold.
     *   Returns the page before it, exclusive, so the seam does not duplicate.
     */
    bars: (symbol: string, timeframe: string, limit = 400, before?: number) =>
      get<BarsResponse>(
        `/v1/chart/bars?symbol=${encodeURIComponent(symbol)}&timeframe=${timeframe}`
        + `&limit=${limit}${before === undefined ? '' : `&before=${before}`}`,
      ),
    analysis: (symbol: string) =>
      get<{ analysis: Analysis; disclaimer: string }>(`/v1/analysis/${encodeURIComponent(symbol)}`),
    accounts: () => get<{ accounts: Account[] }>('/v1/accounts'),
    trades: (accountId: string) =>
      get<{ trades: Trade[]; openTrades: Trade[] }>(`/v1/accounts/${accountId}/trades`),
    analytics: (accountId: string) => get<Analytics>(`/v1/accounts/${accountId}/analytics`),
    rules: (accountId: string) => get<RuleState>(`/v1/accounts/${accountId}/rules`),
    overlay: (accountId: string, symbol: string) =>
      get<{ symbol: string; tradeCount: number; winRate: string | null; netPnl: string; note: string }>(
        `/v1/accounts/${accountId}/overlay/${encodeURIComponent(symbol)}`,
      ),
    // --- strategies, scenarios and the score -----------------------------
    // These are per-user rather than per-account: a strategy is a claim the
    // trader is making, and it does not stop being the same claim because they
    // ran it in a second account.
    strategies: () => get<StrategiesResponse>('/v1/strategies'),
    createStrategy: (body: StrategyInput) =>
      send<StrategyDto>('/v1/strategies', 'POST', body),
    updateStrategy: (id: string, body: Partial<StrategyInput>) =>
      send<StrategyDto>(`/v1/strategies/${id}`, 'PATCH', body),
    deleteStrategy: (id: string) => send<void>(`/v1/strategies/${id}`, 'DELETE'),
    assignTradeStrategy: (tradeId: string, strategyId: string | null) =>
      send<void>(`/v1/trades/${encodeURIComponent(tradeId)}/strategy`, 'PUT', { strategyId }),
    scenarios: () => get<ScenariosResponse>('/v1/scenarios'),

    // --- the daily loop ---------------------------------------------------
    dailyEntries: (from?: string, to?: string) => {
      const q = new URLSearchParams();
      if (from) q.set('from', from);
      if (to) q.set('to', to);
      const qs = q.toString();
      return get<{ entries: NotebookEntryDto[] }>(`/v1/daily/entries${qs ? `?${qs}` : ''}`);
    },
    createEntry: (body: NotebookEntryInput) =>
      send<NotebookEntryDto>('/v1/daily/entries', 'POST', body),
    updateEntry: (id: string, body: Partial<NotebookEntryInput>) =>
      send<NotebookEntryDto>(`/v1/daily/entries/${id}`, 'PATCH', body),
    deleteEntry: (id: string) => send<void>(`/v1/daily/entries/${id}`, 'DELETE'),
    templates: () => get<{ templates: TemplateDto[] }>('/v1/daily/templates'),
    checklist: () => get<ChecklistResponse>('/v1/daily/checklist'),
    saveChecklist: (items: { id?: string; label: string }[]) =>
      send<ChecklistResponse>('/v1/daily/checklist', 'PUT', { items }),
    runChecklist: (day: string, checkedIds: string[]) =>
      send<{ day: string; checkedIds: string[]; complete: boolean }>(
        '/v1/daily/checklist/run', 'PUT', { day, checkedIds }),
    standAside: (day: string, reason: string) =>
      send<{ day: string; reason: string }>('/v1/daily/stood-aside', 'PUT', { day, reason }),
    clearStandAside: (day: string) => send<void>(`/v1/daily/stood-aside/${day}`, 'DELETE'),
    consistency: (days = 90) => get<ConsistencyResponse>(`/v1/daily/consistency?days=${days}`),

    score: () => get<ScoreResponse>('/v1/score'),

    tax: (accountId: string, year: number) =>
      get<Record<string, string | number>>(`/v1/accounts/${accountId}/tax?year=${year}`),
    sync: (accountId: string) =>
      send<{ imported: number; accountId: string }>(`/v1/accounts/${accountId}/sync`, 'POST'),

    // --- identity -------------------------------------------------------------
    me: () => get<Me>('/v1/auth/me'),

    signup: (body: {
      email: string; password: string; firstName?: string; lastName?: string;
      subscriberCategory?: 'non_professional' | 'professional';
    }) => send<AuthSuccess>('/v1/auth/signup', 'POST', { ...body, device: host.deviceHint() }),

    login: (body: { email: string; password: string }) =>
      send<AuthSuccess>('/v1/auth/login', 'POST', { ...body, device: host.deviceHint() }),

    logout: () => send<{ ok: boolean }>('/v1/auth/logout', 'POST'),
    logoutAll: () => send<{ ok: boolean; revoked: number }>('/v1/auth/logout-all', 'POST'),

    sessions: () => get<{ sessions: SessionRow[] }>('/v1/auth/sessions'),
    revokeSession: (id: string) =>
      send<{ ok: boolean }>(`/v1/auth/sessions/${encodeURIComponent(id)}`, 'DELETE'),

    changePassword: (currentPassword: string, newPassword: string) =>
      send<{ ok: boolean; message: string }>(
        '/v1/auth/change-password', 'POST', { currentPassword, newPassword },
      ),

    forgotPassword: (email: string) =>
      send<{ ok: boolean; message: string }>('/v1/auth/forgot-password', 'POST', { email }),
    resetPassword: (token: string, password: string) =>
      send<{ ok: boolean; message: string }>('/v1/auth/reset-password', 'POST', { token, password }),
    verifyEmail: (token: string) =>
      send<{ ok: boolean; message: string }>('/v1/auth/verify-email', 'POST', { token }),
    resendVerification: () =>
      send<{ ok: boolean; message: string }>('/v1/auth/resend-verification', 'POST'),

    // --- billing --------------------------------------------------------------
    plans: () => get<PlansResponse>('/v1/billing/plans'),
    subscription: () => get<SubscriptionResponse>('/v1/billing/subscription'),
    checkout: (plan: 'trader' | 'pro', interval: 'monthly' | 'annual') =>
      send<{ url: string }>('/v1/billing/checkout', 'POST', { plan, interval }),
    portal: () => send<{ url: string }>('/v1/billing/portal', 'POST'),

    // --- watchlist -------------------------------------------------------------
    watchlist: () => get<WatchlistResponse>('/v1/watchlist'),
    saveWatchlist: (roots: readonly string[]) =>
      send<WatchlistResponse>('/v1/watchlist', 'PUT', { roots }),

    /**
     * What the market was doing when this trade was entered.
     *
     * Reconstructed per call rather than read from a snapshot store, which is
     * what makes it work on imported history as well as synced history.
     */
    tradeContext: (accountId: string, tradeId: string) =>
      get<TradeContextDto>(
        `/v1/accounts/${encodeURIComponent(accountId)}/trades/${encodeURIComponent(tradeId)}/context`,
      ),

    // --- journal import ---------------------------------------------------------
    /**
     * Read a file and describe it, writing nothing.
     *
     * Two calls rather than one because the mapping is the user's decision and
     * they cannot make it before seeing their own headers.
     */
    importPreview: (text: string, shape: 'fills' | 'trades') =>
      send<ImportPreview>('/v1/journal/import/preview', 'POST', { text, shape }),

    importJournal: (req: ImportRequest) =>
      send<ImportResultDto>('/v1/journal/import', 'POST', req),

    // --- devices --------------------------------------------------------------
    devices: () => get<DevicesResponse>('/v1/devices'),
    revokeDevice: (id: string) =>
      send<{ revoked: boolean; sessionsEnded: number }>(
        `/v1/devices/${encodeURIComponent(id)}`, 'DELETE',
      ),
    reactivateDevice: (id: string) =>
      send<{ reactivated: boolean }>(
        `/v1/devices/${encodeURIComponent(id)}/reactivate`, 'POST',
      ),

    // --- crash reports (admin) ------------------------------------------------
    //
    // 404 for anyone who is not an admin, deliberately — see routes/telemetry.ts.
    errorGroups: (opts: { limit?: number; range?: string } = {}) =>
      get<{ groups: ErrorGroup[]; range: ResolvedRange }>(
        `/v1/admin/errors?limit=${opts.limit ?? 50}${
          opts.range ? `&range=${encodeURIComponent(opts.range)}` : ''
        }`,
      ),
    errorOccurrences: (fingerprint: string, limit = 25) =>
      get<{ reports: ErrorOccurrence[] }>(
        `/v1/admin/errors/occurrences?fingerprint=${encodeURIComponent(fingerprint)}&limit=${limit}`,
      ),

    // --- admin: operations ----------------------------------------------------
    newsStatus: (range?: string) =>
      get<NewsStatusResponse & { range: ResolvedRange; history: SnapshotDay[] }>(
        `/v1/admin/news-status${range ? `?range=${encodeURIComponent(range)}` : ''}`,
      ),
    aiStatus: () => get<AiStatusResponse>('/v1/admin/ai-status'),
    aiAudit: (opts: { symbol?: string; limit?: number; range?: string } = {}) =>
      get<{ records: AiAuditRecord[]; stats: AiAuditStats; range: ResolvedRange }>(
        `/v1/admin/ai-audit?limit=${opts.limit ?? 50}${
          opts.symbol ? `&symbol=${encodeURIComponent(opts.symbol)}` : ''
        }${opts.range ? `&range=${encodeURIComponent(opts.range)}` : ''}`,
      ),
    entitlementReport: (range?: string) =>
      get<EntitlementReport & { range: ResolvedRange; history: SnapshotDay<EntitlementReport>[] }>(
        `/v1/admin/entitlement-report${range ? `?range=${encodeURIComponent(range)}` : ''}`,
      ),

    // --- on-demand analysis ---------------------------------------------------
    analysisQuota: () =>
      get<{ limit: number; used: number; plan: string }>('/v1/analysis-quota'),
    refreshAnalysis: (symbol: string) =>
      send<{ analysis: Analysis; used: number; limit: number }>(
        `/v1/analysis/${encodeURIComponent(symbol)}/refresh`, 'POST',
      ),

    // --- push (Phase 30 server, Phase 31 client) ------------------------------
    registerPushToken: (deviceId: string, transport: 'expo' | 'apns' | 'fcm', token: string) =>
      send<{ ok: boolean }>('/v1/push/token', 'POST', { deviceId, transport, token }),
    deletePushToken: (deviceId: string) =>
      send<{ ok: boolean }>(`/v1/push/token/${encodeURIComponent(deviceId)}`, 'DELETE'),
    notificationPrefs: () =>
      get<{ prefs: NotificationPrefs }>('/v1/notification-prefs'),
    saveNotificationPrefs: (prefs: Partial<NotificationPrefs>) =>
      send<{ prefs: NotificationPrefs }>('/v1/notification-prefs', 'PUT', prefs),

    // --- credits (ADR-0011) ---------------------------------------------------
    credits: () =>
      get<CreditsResponse>('/v1/credits'),
    personalAnalysis: (accountId: string, symbol: string) =>
      send<PersonalAnalysisResponse>(
        `/v1/accounts/${encodeURIComponent(accountId)}/analysis/${encodeURIComponent(symbol)}/personal`,
        'POST',
      ),

    // --- news and sentiment (Fundamentals) ------------------------------------
    /**
     * The feed. Ranked against the caller's journal by default.
     *
     * `symbol` narrows it to what that instrument listens for — its root, its
     * parent, and its currencies. `order: 'time'` is the escape hatch: ranking
     * is right for a feed and wrong for "what happened in the last hour", and a
     * user who cannot get back to chronological will trust neither view.
     */
    news: (opts: {
      limit?: number; symbol?: string; affects?: readonly string[];
      order?: 'ranked' | 'time';
    } = {}) => {
      const q = new URLSearchParams();
      if (opts.limit) q.set('limit', String(opts.limit));
      if (opts.symbol) q.set('symbol', opts.symbol);
      if (opts.affects?.length) q.set('affects', opts.affects.join(','));
      if (opts.order) q.set('order', opts.order);
      return get<NewsFeedResponse>(`/v1/news?${q.toString()}`);
    },
    /** The most recent release per scheduled source, for the calendar strip. */
    latestReleases: () => get<{ releases: ReleaseCard[] }>('/v1/news/latest'),
    /**
     * The economic calendar: what is coming, plus the last print of each source.
     *
     * Instants are absolute. Count down from them rather than re-deriving a
     * local time from "08:30 ET" — the schedule is stored as wall-clock in a
     * named zone so daylight saving is resolved once, on the server.
     */
    newsCalendar: (opts: { days?: number; limit?: number } = {}) => {
      const q = new URLSearchParams();
      if (opts.days) q.set('days', String(opts.days));
      if (opts.limit) q.set('limit', String(opts.limit));
      return get<NewsCalendarResponse>(`/v1/news/calendar?${q.toString()}`);
    },
    /**
     * What happened the last N times this release landed.
     *
     * Descriptive, never directive. Read `underpowered` before rendering
     * anything: a median over three observations is not a pattern.
     */
    newsReaction: (opts: {
      source: string; symbol: string; minutes?: number;
      verdict?: ReleaseVerdict; limit?: number;
    }) => {
      const q = new URLSearchParams({ source: opts.source, symbol: opts.symbol });
      if (opts.minutes) q.set('minutes', String(opts.minutes));
      if (opts.verdict) q.set('verdict', opts.verdict);
      if (opts.limit) q.set('limit', String(opts.limit));
      return get<ReactionStudy>(`/v1/news/reaction?${q.toString()}`);
    },
    /** Risk appetite off the market feed. See `SentimentReading` before drawing it. */
    sentiment: () => get<SentimentReading>('/v1/sentiment'),

    /** Risk across every account at once. See `PortfolioRiskResponse`. */
    portfolioRisk: () => get<PortfolioRiskResponse>('/v1/accounts/risk'),

    /**
     * Everything a widget can draw, in one call.
     *
     * One round trip because the caller is often a backgrounded app woken by a
     * silent push, with seconds to fetch, write the shared container and hand
     * back control. Four sequential calls is that budget spent on round trips.
     *
     * The response carries every concept, not the selected one — see
     * `WidgetSnapshot`.
     */
    widgetSnapshot: () => get<WidgetSnapshot>('/v1/widget/snapshot'),

    /**
     * How this trader has done in the situation currently on screen.
     *
     * The situation is computed server-side from the same bars the chart is
     * drawing — a client does not get to post its own idea of the regime, or
     * two clients would disagree about the same market. `direction` is the one
     * part the client knows better: it is what the trader is considering, or
     * what they already hold, and is left off when there is nothing open.
     */
    precedent: (symbol: string, direction?: 'long' | 'short') =>
      get<PrecedentResponse>(
        `/v1/precedent/${encodeURIComponent(symbol)}${direction ? `?direction=${direction}` : ''}`,
      ),

    // --- the first-run questionnaire ------------------------------------------
    onboarding: () => get<OnboardingState>('/v1/onboarding'),
    onboardingFirms: () => get<{ firms: OnboardingFirm[] }>('/v1/onboarding/firms'),
    saveOnboarding: (answers: OnboardingAnswersDto, status: 'in_progress' | 'completed' | 'skipped') =>
      send<OnboardingSaveResponse>('/v1/onboarding', 'PUT', { answers, status }),

    // --- preferences ----------------------------------------------------------
    preferences: () => get<PreferencesResponse>('/v1/preferences'),
    savePreferences: (p: Preferences) =>
      send<PreferencesResponse>('/v1/preferences', 'PUT', p),

    // --- drawings -------------------------------------------------------------
    drawings: (symbol: string) =>
      get<DrawingsResponse>(`/v1/drawings?symbol=${encodeURIComponent(symbol)}`),
    saveDrawing: (d: Omit<DrawingDto, 'createdAt'>) =>
      send<DrawingDto>(`/v1/drawings/${encodeURIComponent(d.id)}`, 'PUT', d),
    deleteDrawing: (id: string) =>
      send<{ deleted: boolean }>(`/v1/drawings/${encodeURIComponent(id)}`, 'DELETE'),
    clearDrawings: (symbol: string) =>
      send<{ deleted: number }>(`/v1/drawings?symbol=${encodeURIComponent(symbol)}`, 'DELETE'),

    // --- alerts ---------------------------------------------------------------
    alerts: () => get<AlertsResponse>('/v1/alerts'),
    createAlert: (body: {
      trigger: AlertTrigger; repeating: boolean; cooldownSec: number; note: string;
    }) => send<{ id: string }>('/v1/alerts', 'POST', body),
    deleteAlert: (id: string) =>
      send<{ deleted: boolean }>(`/v1/alerts/${encodeURIComponent(id)}`, 'DELETE'),

    // --- broker connections ---------------------------------------------------
    brokerChallenge: () => get<BrokerChallenge>('/v1/broker/challenge'),
    brokerConnections: () =>
      get<{ connections: BrokerConnectionRow[] }>('/v1/broker/connections'),
    brokerConnect: (values: Record<string, string>, nickname?: string) =>
      send<{ connection: BrokerConnectionRow; accountsImported: number }>(
        '/v1/broker/connect', 'POST', { values, nickname },
      ),
    brokerTest: (id: string) =>
      send<{ ok: boolean; reason: string }>(
        `/v1/broker/connections/${encodeURIComponent(id)}/test`, 'POST',
      ),
    brokerDisconnect: (id: string) =>
      send<{ disconnected: boolean; journalRetained: boolean }>(
        `/v1/broker/connections/${encodeURIComponent(id)}`, 'DELETE',
      ),

    // --- journal --------------------------------------------------------------
    saveJournal: (
      tradeId: string,
      // No `tags`. They are written by `setTradeTags` against the vocabulary;
      // sending them here would give one trade two tag lists that can disagree.
      entry: { notes: string; rating: number | null; emotions: string[] },
    ) => send<JournalEntry>(`/v1/journal/${encodeURIComponent(tradeId)}`, 'PUT', entry),
    journal: (tradeId: string) =>
      get<JournalEntry>(`/v1/journal/${encodeURIComponent(tradeId)}`),

    // --- reporting ------------------------------------------------------------
    // Every report goes through one filter, applied server-side. The client
    // never filters a returned set: two screens filtering the same data their
    // own way is how they come to disagree about the same month.
    reportDimensions: () => get<DimensionsResponse>('/v1/reports/dimensions'),
    runReport: (spec: ReportSpec) => send<ReportResponse>('/v1/reports/run', 'POST', spec),
    winsVsLosses: (dimension: Dimension, filter: TradeFilterDto) =>
      send<WinLossResponse>('/v1/reports/wins-vs-losses', 'POST', { dimension, filter }),
    savedReports: () => get<{ reports: SavedReportDto[] }>('/v1/reports/saved'),
    saveReport: (name: string, spec: ReportSpec) =>
      send<SavedReportDto>('/v1/reports/saved', 'POST', { name, spec }),
    deleteReport: (id: string) => send<void>(`/v1/reports/saved/${encodeURIComponent(id)}`, 'DELETE'),
    breaches: (from?: string) =>
      get<{ breaches: BreachDto[] }>(`/v1/reports/breaches${from ? `?from=${from}` : ''}`),
    /** Everything the journal knows, under the same filter as every report. */
    statistics: (filter: TradeFilterDto) =>
      send<StatisticsResponse>('/v1/reports/statistics', 'POST', { filter }),

    // --- the tag vocabulary --------------------------------------------------
    tags: () => get<TagsResponse>('/v1/tags'),
    tagSuggestions: () => get<TagSuggestionsResponse>('/v1/tags/suggestions'),
    /** Idempotent on (kind, label), case-insensitively. Returns the live tag. */
    createTag: (label: string, kind: TagKind) =>
      send<TagDto>('/v1/tags', 'POST', { label, kind }),
    updateTag: (id: string, patch: { label?: string; archived?: boolean }) =>
      send<TagDto>(`/v1/tags/${encodeURIComponent(id)}`, 'PATCH', patch),
    deleteTag: (id: string) => send<void>(`/v1/tags/${encodeURIComponent(id)}`, 'DELETE'),
    /** Replaces the whole set on the trade. Send what should be there. */
    setTradeTags: (tradeId: string, tagIds: string[]) =>
      send<void>(`/v1/trades/${encodeURIComponent(tradeId)}/tags`, 'PUT', { tagIds }),
    tradeTags: () => get<{ trades: Record<string, string[]> }>('/v1/trades/tags'),

    // --- attachments and sharing ---------------------------------------------
    attachments: (tradeId: string) =>
      get<{ attachments: AttachmentDto[] }>(
        `/v1/trades/${encodeURIComponent(tradeId)}/attachments`),
    /** `data` is base64. PNG, JPEG and WebP only — SVG carries script. */
    uploadAttachment: (body: {
      tradeId: string; contentType: string; data: string; caption?: string;
    }) => send<AttachmentDto>('/v1/attachments', 'POST', body),
    deleteAttachment: (id: string) =>
      send<void>(`/v1/attachments/${encodeURIComponent(id)}`, 'DELETE'),

    shares: () => get<{ shares: ShareDto[] }>('/v1/shares'),
    /** `includeNotes` defaults off server-side and is passed explicitly here. */
    createShare: (tradeId: string, includeNotes: boolean, expiresInDays: number | null) =>
      send<ShareDto>('/v1/shares', 'POST', { tradeId, includeNotes, expiresInDays }),
    revokeShare: (tradeId: string) =>
      send<void>(`/v1/shares/${encodeURIComponent(tradeId)}`, 'DELETE'),

    // --- agents ---------------------------------------------------------------
    agents: () => get<AgentsResponse>('/v1/agents'),
    agentRuns: (agentId?: AgentId, day?: string) => {
      const q = new URLSearchParams();
      if (agentId) q.set('agentId', agentId);
      if (day) q.set('day', day);
      const qs = q.toString();
      return get<{ runs: AgentRunDto[] }>(`/v1/agents/runs${qs ? `?${qs}` : ''}`);
    },
    runAgent: (agentId: AgentId, day?: string) =>
      send<AgentRunDto>(`/v1/agents/${agentId}/run`, 'POST', day ? { day } : {}),
    /** Explicit and additive. Omit `tradeIds` to accept every suggestion. */
    applyAgentTags: (runId: string, tradeIds?: string[]) =>
      send<{ applied: { tradeId: string; tags: string[] }[] }>(
        `/v1/agents/runs/${encodeURIComponent(runId)}/apply`, 'POST',
        tradeIds ? { tradeIds } : {}),

    // --- backtesting ----------------------------------------------------------
    backtestCapabilities: (symbol?: string) =>
      get<BacktestCapabilities>(
        `/v1/backtests/capabilities${symbol ? `?symbol=${encodeURIComponent(symbol)}` : ''}`),
    backtests: (strategyId?: string) =>
      get<{ backtests: BacktestDto[] }>(
        `/v1/backtests${strategyId ? `?strategyId=${encodeURIComponent(strategyId)}` : ''}`),
    runBacktest: (body: BacktestInput) => send<BacktestDto>('/v1/backtests', 'POST', body),
    backtest: (id: string) => get<BacktestDto>(`/v1/backtests/${encodeURIComponent(id)}`),
    deleteBacktest: (id: string) => send<void>(`/v1/backtests/${encodeURIComponent(id)}`, 'DELETE'),
    /** The hypothetical record beside the real one. 422 when unattached. */
    compareBacktest: (id: string) =>
      get<BacktestComparison>(`/v1/backtests/${encodeURIComponent(id)}/compare`),

    // --- workspaces -----------------------------------------------------------
    /**
     * Every layout, plus which one is active and what the panels are.
     *
     * `panels` comes back with them so a picker never hard-codes a list — the
     * set of panels is a property of the build, and a client that carried its
     * own copy would offer one this server does not have.
     *
     * Seeds the presets on first call for an account that has none.
     */
    workspaces: () => get<WorkspacesResponse>('/v1/workspaces'),
    /**
     * Save one, creating it if the id is new.
     *
     * The server normalises on the way IN, so what comes back may differ from
     * what went out — a duplicate panel dropped, an empty column collapsed.
     * Use the response, not the request, as the new state.
     *
     * 409 `too_many_workspaces` past the limit.
     */
    saveWorkspace: (id: string, body: WorkspaceInput) =>
      send<WorkspaceDto>(`/v1/workspaces/${encodeURIComponent(id)}`, 'PUT', body),
    /** 409 `last_workspace` on the only one — a blank app is not a valid state. */
    deleteWorkspace: (id: string) =>
      send<void>(`/v1/workspaces/${encodeURIComponent(id)}`, 'DELETE'),
    /** Which layout the trader is in. A property of the person, not of a layout. */
    setActiveWorkspace: (id: string | null) =>
      send<{ activeWorkspaceId: string | null }>('/v1/workspaces/active', 'PUT', { id }),

    // --- depth and the tape ---------------------------------------------------
    /**
     * The order book. 403 with `remedy: 'exchange_subscription'` when the
     * account holds Level 1 but not depth — which is not something an AEGIS
     * payment fixes.
     */
    depth: (symbol: string, levels = 10) =>
      get<DepthResponse>(`/v1/depth/${encodeURIComponent(symbol)}?levels=${levels}`),
    /** Prints are Level 1. No depth subscription required. */
    tape: (symbol: string, limit = 50, minSize = 0) =>
      get<TapeResponse>(
        `/v1/tape/${encodeURIComponent(symbol)}?limit=${limit}&minSize=${minSize}`),
    /**
     * Volume at price, split by aggressor, on the chart's own bar grid.
     *
     * Level 1 like the tape — a footprint is built from what traded, not from
     * what was resting. The grid cannot be finer than `timeframe`: nothing
     * records where inside a bar the volume sat, so a finer footprint would be
     * invented. 400 for a timeframe above 1h.
     */
    footprint: (
      symbol: string,
      timeframe = '1m',
      limit = 30,
      opts: {
        imbalanceRatio?: number;
        minImbalanceSize?: number;
        /** Ticks per row. Omit and the server picks one the window fits in. */
        rowTicks?: number;
      } = {},
    ) => {
      const q = new URLSearchParams({ timeframe, limit: String(limit) });
      if (opts.imbalanceRatio !== undefined) q.set('imbalanceRatio', String(opts.imbalanceRatio));
      if (opts.minImbalanceSize !== undefined) {
        q.set('minImbalanceSize', String(opts.minImbalanceSize));
      }
      if (opts.rowTicks !== undefined) q.set('rowTicks', String(opts.rowTicks));
      return get<FootprintResponse>(`/v1/footprint/${encodeURIComponent(symbol)}?${q}`);
    },
    /**
     * Where price spent its time, and how much traded there.
     *
     * `period` decides the window: a trading day, the RTH session inside one,
     * a week, or a plain range given in epoch seconds. The trading day comes
     * from the instrument's session template, so it starts the evening before
     * for a future and moves with daylight saving.
     *
     * Read `basis` on every profile before rendering a volume figure.
     */
    profile: (
      symbol: string,
      opts: {
        timeframe?: string;
        period?: 'day' | 'session' | 'week' | 'range';
        limit?: number;
        /** Ticks per row. Omit and the server picks one the window fits in. */
        rowTicks?: number;
        /** Minutes in one TPO period. Thirty is the convention. */
        periodMinutes?: number;
        /** Share of the volume inside the value area. 0.7 is the convention. */
        valueAreaPct?: number;
        /** Epoch seconds, for `period: 'range'`. */
        from?: number;
        to?: number;
      } = {},
    ) => {
      const q = new URLSearchParams();
      for (const [k, v] of Object.entries(opts)) {
        if (v !== undefined) q.set(k, String(v));
      }
      return get<ProfileResponse>(`/v1/profile/${encodeURIComponent(symbol)}?${q}`);
    },
    /* --- custom indicator scripts --- */

    /**
     * Everything this trader can run: what they wrote, and what they installed.
     *
     * A script they wrote appears in BOTH lists — authoring installs it — and
     * the installed copy carries `mine: true` so the client can offer edit and
     * share against it rather than uninstall and report.
     */
    scripts: () => get<ScriptsResponse>('/v1/scripts'),
    /** Create, or update when `id` is given. The server computes the fingerprint. */
    saveScript: (body: {
      id?: string;
      name: string;
      source: string;
      overlay: boolean;
      inputs?: unknown[];
      plots: unknown[];
    }) => send<ScriptSaveResponse>('/v1/scripts', 'POST', body),
    deleteScript: (id: string) => send<void>(`/v1/scripts/${encodeURIComponent(id)}`, 'DELETE'),
    /** Issue a share code, or return the one already issued. */
    shareScript: (id: string) =>
      send<{ script: MyScriptDto }>(`/v1/scripts/${encodeURIComponent(id)}/share`, 'POST'),
    /** Withdraw the link. The code is dropped, so anyone holding it has nothing. */
    unshareScript: (id: string) =>
      send<{ script: MyScriptDto }>(`/v1/scripts/${encodeURIComponent(id)}/unshare`, 'POST'),
    /** What a share code points at. Returns the source, to be read before installing. */
    sharedScript: (code: string) =>
      get<SharedScriptDto>(`/v1/scripts/shared/${encodeURIComponent(code)}`),
    installScript: (code: string) =>
      send<{ script: InstalledScriptDto }>(
        `/v1/scripts/shared/${encodeURIComponent(code)}/install`, 'POST'),
    uninstallScript: (id: string) =>
      send<void>(`/v1/scripts/${encodeURIComponent(id)}/install`, 'DELETE'),
    /** This trader's own settings for an installed script. Never the script itself. */
    setScriptSettings: (
      id: string,
      body: { settings?: Record<string, unknown>; enabled?: boolean },
    ) => send<{ script: InstalledScriptDto }>(
      `/v1/scripts/${encodeURIComponent(id)}/settings`, 'PATCH', body),
    /** Report a shared script. Nothing happens automatically; a person reads it. */
    reportScript: (id: string, reason: string) =>
      send<{ received: boolean }>(
        `/v1/scripts/${encodeURIComponent(id)}/report`, 'POST', { reason }),

    /** The finest real bars under a chart timeframe, for stepping intrabar. */
    fineReplay: (symbol: string, timeframe: string, limit = 2000) =>
      get<FineReplayResponse>(
        `/v1/replay/${encodeURIComponent(symbol)}/fine?timeframe=${timeframe}&limit=${limit}`),
  };

  /**
   * `setUnauthenticatedHandler` registers a callback for any 401. The session
   * provider registers itself rather than this module importing the provider,
   * because a token that expires mid-session surfaces as a 401 on whatever
   * request happened to be in flight — a background analytics poll as easily as
   * a click — and every one of them has to end in the same place.
   */
  return { api, setUnauthenticatedHandler };
}

/** The request surface, for consumers that pass the client around. */
export type Api = ReturnType<typeof createApi>['api'];
